import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  Crown, MessageSquare, FileText, Download, Video, Megaphone,
  Users, Send, Upload, Lock, Sparkles, ArrowRight, Check, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const WELCOME_MESSAGE = `Welcome to the Susan David Inner Circle.
This is your private creator space for exclusive content, inspiration sessions, and early-access materials.`;

export default function InnerCirclePage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [info, setInfo] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  
  // Room content
  const [messages, setMessages] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [files, setFiles] = useState([]);
  const [members, setMembers] = useState([]);
  
  // Form states
  const [chatMessage, setChatMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({ title: '', content: '' });
  const [creatingAnnouncement, setCreatingAnnouncement] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileDescription, setFileDescription] = useState('');
  
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
    // Check for subscription success
    if (searchParams.get('subscription') === 'success') {
      const sessionId = searchParams.get('session_id');
      if (sessionId) {
        activateMembership(sessionId);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for new messages every 10 seconds
  useEffect(() => {
    if (status?.has_access) {
      const interval = setInterval(() => {
        loadMessages();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [status?.has_access]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statusRes, infoRes] = await Promise.all([
        api.get('/inner-circle/status'),
        api.get('/inner-circle/info')
      ]);
      setStatus(statusRes.data);
      setInfo(infoRes.data);
      
      if (statusRes.data.has_access) {
        await loadRoomContent();
      }
    } catch (err) {
      console.error('Failed to load Inner Circle data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRoomContent = async () => {
    try {
      const [messagesRes, announcementsRes, filesRes, membersRes] = await Promise.all([
        api.get('/inner-circle/chat'),
        api.get('/inner-circle/announcements'),
        api.get('/inner-circle/files'),
        api.get('/inner-circle/members')
      ]);
      setMessages(messagesRes.data);
      setAnnouncements(announcementsRes.data);
      setFiles(filesRes.data);
      setMembers(membersRes.data);
    } catch (err) {
      console.error('Failed to load room content:', err);
    }
  };

  const loadMessages = async () => {
    try {
      const res = await api.get('/inner-circle/chat');
      setMessages(res.data);
    } catch {}
  };

  const activateMembership = async (sessionId) => {
    try {
      const res = await api.post('/inner-circle/activate', { session_id: sessionId });
      if (res.data.is_member) {
        toast.success('Welcome to the Inner Circle!');
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate membership');
    }
  };

  const handleSubscribe = async () => {
    try {
      setSubscribing(true);
      const res = await api.post('/inner-circle/subscribe');
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start subscription');
      setSubscribing(false);
    }
  };

  const sendMessage = async () => {
    if (!chatMessage.trim()) return;
    try {
      setSendingMessage(true);
      await api.post('/inner-circle/chat', { content: chatMessage.trim() });
      setChatMessage('');
      loadMessages();
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const createAnnouncement = async () => {
    if (!announcementForm.title.trim() || !announcementForm.content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }
    try {
      setCreatingAnnouncement(true);
      await api.post('/inner-circle/announcements', announcementForm);
      setAnnouncementForm({ title: '', content: '' });
      toast.success('Announcement posted');
      const res = await api.get('/inner-circle/announcements');
      setAnnouncements(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create announcement');
    } finally {
      setCreatingAnnouncement(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploadingFile(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', fileDescription);
      
      await api.post('/inner-circle/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('File uploaded');
      setFileDescription('');
      const res = await api.get('/inner-circle/files');
      setFiles(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const res = await api.get(`/inner-circle/files/${fileId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download file');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="inner-circle-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  // Non-member view - subscription page
  if (!status?.has_access) {
    return (
      <div className="animate-in max-w-3xl mx-auto" data-testid="inner-circle-subscribe">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-gold/10 text-gold px-4 py-1.5 rounded-full text-xs font-medium mb-4">
            <Crown size={14} /> Private Membership
          </div>
          <h1 className="font-serif text-3xl md:text-4xl mb-4">{info?.name || 'Susan David Inner Circle'}</h1>
          <p className="text-muted-foreground text-sm md:text-base max-w-xl mx-auto">
            {WELCOME_MESSAGE}
          </p>
        </div>

        {/* Pricing Card */}
        <Card className="border-gold/30 bg-gradient-to-b from-gold/5 to-transparent rounded-sm mb-8">
          <CardHeader className="text-center pb-4">
            <CardTitle className="font-serif text-2xl flex items-center justify-center gap-2">
              <Crown className="text-gold" size={24} />
              Join the Inner Circle
            </CardTitle>
            <CardDescription>Unlock exclusive creator benefits</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Price */}
            <div className="text-center">
              <div className="text-4xl font-serif text-obsidian">
                €{info?.price_eur || 5}<span className="text-lg text-muted-foreground">/month</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{info?.member_count || 0} members</p>
            </div>

            {/* Features */}
            <div className="space-y-3">
              {(info?.features || [
                'Exclusive group chat',
                'Premium file sharing',
                'Digital downloads access',
                'Live video sessions (WebRTC)',
                'Creator announcements'
              ]).map((feature, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
                    <Check size={12} className="text-gold" />
                  </div>
                  {feature}
                </div>
              ))}
            </div>

            {/* Subscribe Button */}
            <Button
              data-testid="subscribe-btn"
              onClick={handleSubscribe}
              disabled={subscribing}
              className="w-full rounded-sm h-12 uppercase tracking-wider text-xs bg-gold text-obsidian hover:bg-obsidian hover:text-gold transition-colors"
            >
              {subscribing ? (
                <><Loader2 size={14} className="animate-spin mr-2" /> Processing...</>
              ) : (
                <>Join Inner Circle <ArrowRight size={14} className="ml-2" /></>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* What's Inside Preview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: MessageSquare, label: 'Group Chat' },
            { icon: FileText, label: 'Exclusive Files' },
            { icon: Video, label: 'Live Sessions' },
            { icon: Megaphone, label: 'Announcements' }
          ].map((item, i) => (
            <div key={i} className="text-center p-4 border border-border/40 rounded-sm">
              <item.icon size={24} className="mx-auto mb-2 text-gold" />
              <p className="text-xs font-medium">{item.label}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Member view - full room access
  return (
    <div className="animate-in" data-testid="inner-circle-room">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Crown className="text-gold" size={24} />
            <h1 className="font-serif text-2xl">{info?.name || 'Susan David Inner Circle'}</h1>
            <Badge className="bg-gold/20 text-gold border-gold/30 text-[10px]">MEMBER</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{members.length} members</p>
        </div>
        <Link to="/video-call">
          <Button variant="outline" className="rounded-sm text-xs border-gold text-gold hover:bg-gold hover:text-obsidian">
            <Video size={14} className="mr-2" /> Live Session
          </Button>
        </Link>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="chat" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 h-auto p-1 bg-mist rounded-sm">
          <TabsTrigger value="chat" className="text-xs py-2 data-[state=active]:bg-obsidian data-[state=active]:text-white rounded-sm" data-testid="tab-chat">
            <MessageSquare size={14} className="mr-1.5" /> Chat
          </TabsTrigger>
          <TabsTrigger value="announcements" className="text-xs py-2 data-[state=active]:bg-obsidian data-[state=active]:text-white rounded-sm" data-testid="tab-announcements">
            <Megaphone size={14} className="mr-1.5" /> Announcements
          </TabsTrigger>
          <TabsTrigger value="files" className="text-xs py-2 data-[state=active]:bg-obsidian data-[state=active]:text-white rounded-sm" data-testid="tab-files">
            <FileText size={14} className="mr-1.5" /> Files
          </TabsTrigger>
          <TabsTrigger value="members" className="text-xs py-2 data-[state=active]:bg-obsidian data-[state=active]:text-white rounded-sm" data-testid="tab-members">
            <Users size={14} className="mr-1.5" /> Members
          </TabsTrigger>
        </TabsList>

        {/* Chat Tab */}
        <TabsContent value="chat">
          <Card className="border-border/40 rounded-sm">
            <CardContent className="p-0">
              {/* Messages */}
              <ScrollArea className="h-[400px] p-4">
                <div className="space-y-4">
                  {/* Welcome message */}
                  <div className="bg-gold/10 p-4 rounded-sm text-center">
                    <Crown size={20} className="mx-auto mb-2 text-gold" />
                    <p className="text-sm">{WELCOME_MESSAGE}</p>
                  </div>
                  
                  {messages.map(m => (
                    <div key={m.id} className="flex gap-3" data-testid={`chat-msg-${m.id}`}>
                      <Avatar className="h-8 w-8 border shrink-0">
                        <AvatarFallback className="bg-obsidian text-white text-[10px]">
                          {(m.user_name || 'U')[0].toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm font-medium">{m.user_name}</span>
                          <span className="text-[10px] text-muted-foreground">
                            {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5">{m.content}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
              </ScrollArea>
              
              {/* Input */}
              <div className="p-3 border-t border-border/40 flex gap-2">
                <Input
                  data-testid="chat-input"
                  placeholder="Type a message..."
                  value={chatMessage}
                  onChange={e => setChatMessage(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendMessage()}
                  className="rounded-sm text-sm h-10"
                />
                <Button
                  data-testid="chat-send-btn"
                  onClick={sendMessage}
                  disabled={!chatMessage.trim() || sendingMessage}
                  className="rounded-sm h-10 px-4 bg-obsidian hover:bg-gold hover:text-obsidian"
                >
                  {sendingMessage ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Announcements Tab */}
        <TabsContent value="announcements">
          <div className="space-y-4">
            {/* Admin: Create Announcement */}
            {user?.role === 'admin' && (
              <Card className="border-gold/30 rounded-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Megaphone size={14} className="text-gold" /> Post Announcement
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Input
                    data-testid="announcement-title"
                    placeholder="Announcement title"
                    value={announcementForm.title}
                    onChange={e => setAnnouncementForm(prev => ({ ...prev, title: e.target.value }))}
                    className="rounded-sm text-sm h-10"
                  />
                  <Textarea
                    data-testid="announcement-content"
                    placeholder="Announcement content..."
                    value={announcementForm.content}
                    onChange={e => setAnnouncementForm(prev => ({ ...prev, content: e.target.value }))}
                    className="rounded-sm text-sm min-h-[80px]"
                  />
                  <Button
                    data-testid="announcement-submit"
                    onClick={createAnnouncement}
                    disabled={creatingAnnouncement}
                    className="rounded-sm text-xs h-9 bg-gold text-obsidian hover:bg-obsidian hover:text-gold"
                  >
                    {creatingAnnouncement ? <Loader2 size={12} className="animate-spin mr-2" /> : null}
                    Post Announcement
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Announcements List */}
            {announcements.length === 0 ? (
              <Card className="border-border/40 rounded-sm">
                <CardContent className="py-8 text-center text-muted-foreground text-sm">
                  No announcements yet. Stay tuned!
                </CardContent>
              </Card>
            ) : (
              announcements.map(a => (
                <Card key={a.id} className="border-border/40 rounded-sm" data-testid={`announcement-${a.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="font-serif text-base">{a.title}</CardTitle>
                      <Badge variant="outline" className="text-[9px]">
                        {new Date(a.created_at).toLocaleDateString()}
                      </Badge>
                    </div>
                    <CardDescription className="text-xs">By {a.author_name}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{a.content}</p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Files Tab */}
        <TabsContent value="files">
          <div className="space-y-4">
            {/* Admin: Upload File */}
            {user?.role === 'admin' && (
              <Card className="border-gold/30 rounded-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Upload size={14} className="text-gold" /> Upload File
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Input
                    data-testid="file-description"
                    placeholder="File description (optional)"
                    value={fileDescription}
                    onChange={e => setFileDescription(e.target.value)}
                    className="rounded-sm text-sm h-10"
                  />
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="file-input"
                    />
                    <Button
                      data-testid="file-upload-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingFile}
                      variant="outline"
                      className="rounded-sm text-xs h-9"
                    >
                      {uploadingFile ? <Loader2 size={12} className="animate-spin mr-2" /> : <Upload size={12} className="mr-2" />}
                      Choose File
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Files List */}
            {files.length === 0 ? (
              <Card className="border-border/40 rounded-sm">
                <CardContent className="py-8 text-center text-muted-foreground text-sm">
                  No files uploaded yet.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {files.map(f => (
                  <Card key={f.id} className="border-border/40 rounded-sm" data-testid={`file-${f.id}`}>
                    <CardContent className="py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-mist rounded-sm flex items-center justify-center">
                          <FileText size={18} className="text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{f.filename}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {f.description || 'No description'} • {(f.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <Button
                        data-testid={`download-${f.id}`}
                        onClick={() => downloadFile(f.id, f.filename)}
                        variant="outline"
                        size="sm"
                        className="rounded-sm text-xs h-8"
                      >
                        <Download size={12} className="mr-1" /> Download
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Members Tab */}
        <TabsContent value="members">
          <Card className="border-border/40 rounded-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users size={14} className="text-gold" /> {members.length} Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {members.map(m => (
                  <div key={m.id} className="flex items-center gap-3 p-2 hover:bg-mist rounded-sm" data-testid={`member-${m.id}`}>
                    <Avatar className="h-8 w-8 border">
                      <AvatarFallback className="bg-obsidian text-white text-[10px]">
                        {(m.user_name || 'U')[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{m.user_name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        Joined {new Date(m.joined_at).toLocaleDateString()}
                      </p>
                    </div>
                    {m.user_id === user?.id && (
                      <Badge className="text-[9px] bg-gold/20 text-gold">You</Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
